import { X, MapPin, Info, Phone, Navigation } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { navigationService } from '@/services/NavigationService'

interface PlaceInfoPopupProps {
  feature: __esri.Graphic
  screenPoint: { x: number; y: number }
  onClose: () => void
}

export function PlaceInfoPopup({ feature, screenPoint, onClose }: PlaceInfoPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ x: screenPoint.x, y: screenPoint.y })

  // Calculate popup position to ensure it stays within viewport
  useEffect(() => {
    if (!popupRef.current) return

    const popup = popupRef.current
    const rect = popup.getBoundingClientRect()
    const padding = 20
    
    let x = screenPoint.x + 10 // Offset from cursor
    let y = screenPoint.y - rect.height / 2 // Center vertically on click point

    // Check right edge
    if (x + rect.width + padding > window.innerWidth) {
      x = screenPoint.x - rect.width - 10 // Show on left side
    }

    // Check bottom edge
    if (y + rect.height + padding > window.innerHeight) {
      y = window.innerHeight - rect.height - padding
    }

    // Check top edge
    if (y < padding) {
      y = padding
    }

    setPosition({ x, y })
  }, [screenPoint])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    // Delay to prevent immediate close from the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose])

  // Extract attributes from feature
  const attributes = feature.attributes || {}
  
  // Filter out internal fields and prepare display data
  const displayAttributes = Object.entries(attributes)
    .filter(([key]) => !key.startsWith('_') && key !== 'objectid' && key !== 'globalid')
    .map(([key, value]) => ({
      key: key.toLowerCase(),
      label: formatAttributeLabel(key),
      value: formatAttributeValue(value),
      rawValue: value as string | number | boolean | null | undefined
    }))

  // Handle directions
  const handleDirections = async (address: string) => {
    try {
      await navigationService.navigateFromCurrentLocation(address)
    } catch (error) {
      console.error('Failed to open directions:', error)
      // Fallback - open Google Maps with just the destination
      const encodedAddress = encodeURIComponent(address)
      window.open(`https://www.google.com/maps/search/${encodedAddress}`, '_blank')
    }
  }

  // Handle phone call
  const handleCall = (phoneNumber: string) => {
    // Clean phone number (remove spaces, dashes, etc.)
    const cleanNumber = phoneNumber.replace(/\D/g, '')
    window.location.href = `tel:${cleanNumber}`
  }

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={popupRef}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
        className="fixed z-50"
        style={{ 
          left: `${position.x}px`, 
          top: `${position.y}px`,
          maxWidth: '320px'
        }}
      >
        <Card className="shadow-2xl bg-white">
          {/* Header */}
          <div className="flex items-start justify-between p-4 pb-0">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">Thông tin địa điểm</h3>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose} 
              className="h-8 w-8 -mt-1 -mr-1"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Separator className="my-3" />

          {/* Attributes */}
          <div className="px-4 pb-4 space-y-3">
            {displayAttributes.length > 0 ? (
              displayAttributes.map((attr, index) => (
                <div key={index} className="space-y-1">
                  <p className="text-xs text-gray-500">{attr.label}</p>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 flex-1">{attr.value}</p>
                    {/* Directions button for address */}
                    {attr.key === 'diachi' && attr.rawValue && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDirections(String(attr.rawValue))}
                        className="h-7 w-7 hover:bg-gray-100"
                        title="Chỉ đường"
                      >
                        <Navigation className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {/* Call button for phone */}
                    {(attr.key === 'sdt' || attr.key === 'duongdaynong') && attr.rawValue && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleCall(String(attr.rawValue))}
                        className="h-7 w-7 hover:bg-gray-100"
                        title="Gọi điện"
                      >
                        <Phone className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex items-center gap-2 text-gray-500">
                <Info className="h-4 w-4" />
                <p className="text-sm">Không có thông tin chi tiết</p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

// Helper function to format attribute labels to Vietnamese
function formatAttributeLabel(key: string): string {
  const labelMap: Record<string, string> = {
    ten: 'Tên',
    diachi: 'Địa chỉ',
    chinhanh: 'Chi nhánh',
    phamvigiaiquyet: 'Phạm vi giải quyết',
    duongdaynong: 'Đường dây nóng',
    xa: 'Xã/Phường',
    quan: 'Quận/Huyện',
    tinh: 'Tỉnh/Thành phố',
    sdt: 'Số điện thoại',
    email: 'Email',
    website: 'Website',
    ghichu: 'Ghi chú',
    mota: 'Mô tả',
    loai: 'Loại',
    trangthai: 'Trạng thái',
    ngaytao: 'Ngày tạo',
    ngaysua: 'Ngày sửa',
    // Add more mappings as needed
  }

  return labelMap[key.toLowerCase()] || 
    key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
}

// Helper function to format attribute values
function formatAttributeValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'N/A'
  }
  
  // Handle dates
  if (typeof value === 'number' && value > 1000000000000) {
    return new Date(value).toLocaleDateString('vi-VN')
  }
  
  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Có' : 'Không'
  }
  
  return String(value)
}
